// Chunked / resumable file uploads
//
// Files ≤ SIMPLE_LIMIT go through the existing single-request /files/upload
// endpoint (fast path, keeps the round-trip count low). Larger files are
// split into fixed-size chunks and sent one at a time via the resumable
// /files/upload/{id}/chunks/{i} endpoints — this bypasses proxy/body-size
// limits, keeps memory flat on the server, and gives us a natural resume
// point on transient network failures.
import { api } from "./api";

const SIMPLE_LIMIT = 100 * 1024 * 1024; // 100 MB — matches /files/upload cap
const CHUNK_RETRIES = 3;
const RETRY_BACKOFF_MS = 800;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function readSlice(file, start, end) {
  return await file.slice(start, end).arrayBuffer();
}

async function simpleUpload({ file, meta, onProgress, signal }) {
  const fd = new FormData();
  fd.append("file", file);
  if (meta?.folder_id) fd.append("folder_id", meta.folder_id);
  if (meta?.linked_type) fd.append("linked_type", meta.linked_type);
  if (meta?.linked_id) fd.append("linked_id", meta.linked_id);
  const url = meta?.version_of_file_id
    ? `/files/${meta.version_of_file_id}/versions`
    : "/files/upload";
  const { data } = await api.post(url, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 0,
    signal,
    onUploadProgress: (evt) => {
      if (!onProgress) return;
      const loaded = evt.loaded || 0;
      const total = evt.total || file.size;
      onProgress({ loaded, total, percent: total ? loaded / total : 0 });
    },
  });
  return data;
}

async function chunkedUpload({ file, meta, onProgress, signal }) {
  // 1) Init session
  const initBody = {
    filename: file.name,
    total_size: file.size,
    content_type: file.type || "application/octet-stream",
    folder_id: meta?.folder_id || null,
    linked_type: meta?.linked_type || null,
    linked_id: meta?.linked_id || null,
    version_of_file_id: meta?.version_of_file_id || null,
  };
  const { data: init } = await api.post("/files/upload/init", initBody, { signal });
  const uploadId = init.upload_id;
  const chunkSize = init.chunk_size;
  const totalChunks = init.total_chunks;

  // Support resume if the caller passed a pre-existing upload_id — not used
  // in this UI yet, but the endpoint set is ready for it.
  const alreadyReceived = new Set(init.received_chunks || []);
  let loaded = alreadyReceived.size * chunkSize; // approximate

  const notify = () => {
    if (!onProgress) return;
    onProgress({
      loaded: Math.min(loaded, file.size),
      total: file.size,
      percent: Math.min(1, loaded / file.size),
    });
  };
  notify();

  try {
    for (let i = 0; i < totalChunks; i++) {
      if (alreadyReceived.has(i)) continue;
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const buf = await readSlice(file, start, end);

      let attempt = 0;
      while (true) {
        try {
          await api.put(`/files/upload/${uploadId}/chunks/${i}`, buf, {
            headers: { "Content-Type": "application/octet-stream" },
            timeout: 0,
            signal,
            // We already know the chunk size — the per-chunk axios progress
            // is noisy on small chunks, so we bump the outer counter after
            // each successful chunk instead.
          });
          break;
        } catch (err) {
          if (signal?.aborted) throw err;
          attempt += 1;
          if (attempt > CHUNK_RETRIES) throw err;
          await sleep(RETRY_BACKOFF_MS * attempt);
        }
      }
      loaded += end - start;
      notify();
    }

    // 2) Finalize
    const { data: fileDoc } = await api.post(
      `/files/upload/${uploadId}/complete`,
      {},
      { signal, timeout: 0 },
    );
    return fileDoc;
  } catch (err) {
    // Best-effort abort on the server so the temp chunks + session record
    // are released immediately, rather than waiting for the 24h TTL sweep.
    api.delete(`/files/upload/${uploadId}`).catch(() => {});
    throw err;
  }
}

/**
 * Upload a single File to the backend, picking the right transport based on
 * size. Returns the resulting file document.
 *
 * @param {File} file
 * @param {object} meta - { folder_id?, linked_type?, linked_id?, version_of_file_id? }
 * @param {{ onProgress?: (p:{loaded:number,total:number,percent:number})=>void, signal?: AbortSignal }} opts
 */
export async function uploadFileSmart(file, meta = {}, opts = {}) {
  if (!file) throw new Error("No file");
  const { onProgress, signal } = opts;
  if (file.size <= SIMPLE_LIMIT) {
    return simpleUpload({ file, meta, onProgress, signal });
  }
  return chunkedUpload({ file, meta, onProgress, signal });
}

export const UPLOAD_SIMPLE_LIMIT = SIMPLE_LIMIT;
export const UPLOAD_MAX_SIZE = 2 * 1024 * 1024 * 1024;
